/**
 * ExecutionMetadata — Key-value metadata display for a transmission execution.
 * Renders the top-level fields returned by GET /execution/{executionArn}.
 */
import Badge from '@cloudscape-design/components/badge';
import KeyValuePairs from '@cloudscape-design/components/key-value-pairs';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import { ExecutionDetailData } from 'src/store/slices/execution/executionState';
import { formatTimestamp, formatLatency } from 'src/utils/formatters';
import { CopyableText } from 'src/components/common/CopyableText';

interface ExecutionMetadataProps {
  /** Full execution data from the API. */
  data: ExecutionDetailData;
  /** The original transmission row item (fallback for fields the API may not return). */
  item: { executionArn?: string; fileName?: string; bank?: string; messageType?: string };
}

/** Maps a beacon state string to a Cloudscape StatusIndicator type. */
const stateToIndicatorType = (state: string | null): 'success' | 'error' | 'in-progress' => {
  if (state === 'SUCCESS') return 'success';
  if (state === 'FAILED') return 'error';
  return 'in-progress';
};

export const ExecutionMetadata = ({ data, item }: ExecutionMetadataProps) => (
  <KeyValuePairs
    columns={2}
    items={[
      { label: 'File Name', value: <CopyableText value={data.fileName || item.fileName || '-'} /> },
      { label: 'Execution ARN', value: <CopyableText value={item.executionArn || '-'} /> },
      { label: 'Bank', value: data.bank || item.bank || '—' },
      { label: 'Direction', value: data.direction ? <Badge color="blue">{data.direction}</Badge> : '—' },
      { label: 'Message Type', value: (data.messageType || item.messageType) ? <Badge>{data.messageType || item.messageType}</Badge> : '—' },
      { label: 'Data Source', value: data.dataSource ? <Badge>{data.dataSource}</Badge> : '—' },
      { label: 'State', value: (
        <StatusIndicator type={stateToIndicatorType(data.beaconTransmissionState)}>
          {data.beaconTransmissionState || 'UNKNOWN'}
        </StatusIndicator>
      )},
      { label: 'Status', value: data.beaconTransmissionStatus || '—' },
      { label: 'Created', value: formatTimestamp(data.createdAt) },
      { label: 'Updated', value: formatTimestamp(data.updatedAt) },
      { label: 'Total E2E Latency', value: formatLatency(data.totalE2ELatencyMs) },
    ]}
  />
);
